import { defineStore } from 'pinia';

import { createPendingEntries } from '@/modules/companyNeed/helpers/pendingEntries';
import {
	getWorkersSelectionQuantity,
	isSameWorkersSelection,
	shiftWorkersSelection,
} from '@/modules/companyNeed/helpers/workersSelection';
import { WorkerMissionStatus } from '@/types/mission';

import type { WorkersSelectionCount } from '@/modules/companyNeed/helpers/workersSelection';
import type {
	MissionAction,
	MissionList,
	MissionStatus,
} from '@/modules/companyNeed/views/details/missions/types';

export interface MissionRowPatch {
	status?: MissionStatus;
	name?: string;
	totalPositions?: number;
	periods?: MissionList['periods'];
	city?: string;
	contractType?: string;
	confirmedWorkers?: number;
	/** Actions no longer allowed once the command is applied. */
	removedActions?: MissionAction[];
}

interface PatchableMission {
	id: string;
	status: string;
	name: string;
	totalPositions: number;
	city?: string;
	contractType: string;
	confirmedWorkers?: number;
	availableActions: MissionAction[];
}

interface MissionListRow extends PatchableMission {
	workersSelection: WorkersSelectionCount[];
}

const getProposedQuantity = (mission: MissionListRow): number =>
	getWorkersSelectionQuantity(
		mission.workersSelection,
		WorkerMissionStatus.PROPOSED
	);

// Periods and actions are not compared: their server format differs from the local one.
const isPatchApplied = (
	mission: PatchableMission,
	patch: MissionRowPatch
): boolean =>
	(patch.status === undefined || mission.status === patch.status) &&
	(patch.name === undefined || mission.name === patch.name) &&
	(patch.totalPositions === undefined ||
		mission.totalPositions === patch.totalPositions) &&
	(patch.city === undefined || mission.city === patch.city) &&
	(patch.contractType === undefined ||
		mission.contractType.toUpperCase() === patch.contractType.toUpperCase()) &&
	(patch.confirmedWorkers === undefined ||
		mission.confirmedWorkers === patch.confirmedWorkers);

/**
 * Mission changes applied optimistically (validate, cancel, edit, worker status
 * changes, added workers), shared by every view displaying missions. A patch
 * overrides the query-side row until the projection returns the same values or
 * PENDING_TTL_MS has elapsed.
 */
export const useMissionPatchStore = defineStore('missionPatch', () => {
	const patches = createPendingEntries<MissionRowPatch>();
	/** Workers count per status, after worker status changes. */
	const selections = createPendingEntries<WorkersSelectionCount[]>();
	/** Minimum proposed workers count per mission, after workers were added. */
	const proposedFloors = createPendingEntries<number>();

	/** Merges the patch over any pending one and returns its rollback. */
	const applyPatch = (missionId: string, patch: MissionRowPatch) => {
		const previous = patches.get(missionId);
		return patches.set(missionId, {
			...previous,
			...patch,
			removedActions: [
				...(previous?.removedActions ?? []),
				...(patch.removedActions ?? []),
			],
		});
	};

	const withPatch = <T extends PatchableMission>(mission: T): T => {
		const patch = patches.get(mission.id);
		if (!patch) return mission;

		const { removedActions = [], ...fields } = patch;
		const definedFields = Object.fromEntries(
			Object.entries(fields).filter(([, value]) => value !== undefined)
		);
		return {
			...mission,
			...definedFields,
			availableActions: mission.availableActions.filter(
				(action) => !removedActions.includes(action)
			),
		} as T;
	};

	/** Counts one more proposed worker, starting from the count known by the caller. */
	const bumpProposed = (missionId: string, knownQuantity: number) => {
		const selection = selections.get(missionId);
		if (selection) {
			selections.set(
				missionId,
				shiftWorkersSelection(selection, null, WorkerMissionStatus.PROPOSED)
			);
			return;
		}
		const current = Math.max(knownQuantity, proposedFloors.get(missionId) ?? 0);
		proposedFloors.set(missionId, current + 1);
	};

	/**
	 * Sets the workers count per status, which already includes the added workers:
	 * the proposed floor is dropped, then restored by the returned rollback.
	 */
	const applyWorkersSelection = (
		missionId: string,
		selection: WorkersSelectionCount[]
	) => {
		const previousFloor = proposedFloors.get(missionId);
		proposedFloors.remove(missionId);
		const rollbackSelection = selections.set(missionId, selection);
		return () => {
			rollbackSelection();
			if (previousFloor !== undefined)
				proposedFloors.set(missionId, previousFloor);
		};
	};

	const withWorkersSelection = <T extends MissionListRow>(mission: T): T => {
		const selection = selections.get(mission.id);
		const base = selection
			? { ...mission, workersSelection: selection }
			: mission;
		const floor = proposedFloors.get(mission.id);
		if (floor === undefined || getProposedQuantity(base) >= floor) return base;

		const others = base.workersSelection.filter(
			(entry) => entry.workerStatus !== WorkerMissionStatus.PROPOSED
		);
		return {
			...base,
			workersSelection: [
				...others,
				{ workerStatus: WorkerMissionStatus.PROPOSED, quantity: floor },
			],
		};
	};

	const reconcile = (missions: MissionListRow[]) => {
		const serverById = new Map(
			missions.map((mission) => [mission.id, mission])
		);
		patches.reconcile((id, patch) => {
			const serverMission = serverById.get(id);
			return !!serverMission && isPatchApplied(serverMission, patch);
		});
		selections.reconcile((id, selection) => {
			const serverMission = serverById.get(id);
			return (
				!!serverMission &&
				isSameWorkersSelection(serverMission.workersSelection, selection)
			);
		});
		proposedFloors.reconcile((id, floor) => {
			const serverMission = serverById.get(id);
			return !!serverMission && getProposedQuantity(serverMission) >= floor;
		});
	};

	return {
		applyPatch,
		withPatch,
		bumpProposed,
		applyWorkersSelection,
		withWorkersSelection,
		reconcile,
	};
});
